import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Plot from "react-plotly.js";
import "../styles/BoxPlot.scss";
import line_chart_white from "../assets/line-chart-white.png";
import { carColors } from "../utils/carColors";

export const BoxPlotChart = ({ season }) => {
        const [isLoading, setIsLoading] = useState(true);
        const [carData, setCarData] = useState([]);
        const [lastUpdated, setLastUpdated] = useState("");
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
        const [displayRaceBest, setDisplayRaceBest] = useState(false);
        const navigate = useNavigate();

        useEffect(() => {
                const checkMobile = () => setIsMobile(window.innerWidth <= 768);
                window.addEventListener("resize", checkMobile);
                return () => window.removeEventListener("resize", checkMobile);
        }, []);

        useEffect(() => {
                const fetchTracks = async () => {
                        const response = await fetch(`https://iracing6-backend.herokuapp.com/api/series-basic-info/series-basic-info/${season}`);
                        const jsonData = await response.json();
                        if (response.ok) {
                                let returnArray = [];
                                for (const week of jsonData[0].schedule) {
                                        returnArray.push(week.track.track_name);
                                }
                                // url encode the returnArray track names
                                returnArray = returnArray.map((track) => encodeURIComponent(track));
                                return returnArray;
                        } else {
                                console.error(`Failed to fetch tracks for season: ${season}. Reason: ${jsonData.error || "Unknown"}`);
                        }
                };
                const fetchData = async () => {
                        setIsLoading(true);
                        const allData = [];
                        for (const track of await fetchTracks()) {
                                try {
                                        const response = await fetch(`https://iracing6-backend.herokuapp.com/api/times-by-car/seasons/${season}/tracks/${track}`);
                                        const jsonData = await response.json();
                                        if (response.ok) {
                                                allData.push(...jsonData);
                                        } else {
                                                console.error(`Failed to fetch data for track: ${track}. Reason: ${jsonData.error || "Unknown"}`);
                                        }
                                } catch (error) {
                                        console.error(`Failed to fetch data for track: ${track}. Error: ${error.message}`);
                                }
                        }
                        setCarData(allData);
                        setIsLoading(false);
                };
                fetchTracks();
                fetchData();
        }, [season]);

        useEffect(() => {
                const fetchDate = async () => {
                        const response = await fetch("https://iracing6-backend.herokuapp.com/api/times-by-car/last-updated");
                        const jsonData = await response.json();
                        setLastUpdated(jsonData);
                };
                fetchDate();
        }, []);

        function filterOutliers(data, factor = 1.5) {
                const q1 = percentile(data, 25);
                const q3 = percentile(data, 75);
                const iqr = q3 - q1;
                const lowerBound = q1 - factor * iqr;
                const upperBound = q3 + factor * iqr;
                return data.filter((value) => value >= lowerBound && value <= upperBound);
        }

        function percentile(data, p) {
                const sorted = [...data].sort((a, b) => a - b);
                const index = (p / 100) * sorted.length;
                if (Number.isInteger(index)) {
                        return (sorted[index - 1] + sorted[index]) / 2;
                } else {
                        return sorted[Math.floor(index)];
                }
        }

        const groupedCarData = carData.reduce((acc, car) => {
                if (!acc[car.track_name]) {
                        acc[car.track_name] = [];
                }
                acc[car.track_name].push(car);
                return acc;
        }, {});

        const handleCurrentWeekLink = (name) => {
                navigate(`/charts/SeriesLapTimeChart/${season}`);
        };

        const createPlots = (carData, dataKey, titleSuffix) => {
                return Object.entries(carData).map(([trackName, trackCarData]) => {
                        const filteredCarData = trackCarData.map((car) => ({
                                ...car,
                                [dataKey]: filterOutliers(car[dataKey]),
                        }));

                        const medianLapTimes = filteredCarData.map((car) => {
                                return percentile(car[dataKey], 50);
                        });

                        filteredCarData.sort((a, b) => {
                                const aIndex = filteredCarData.indexOf(a);
                                const bIndex = filteredCarData.indexOf(b);
                                return medianLapTimes[bIndex] - medianLapTimes[aIndex];
                        });

                        const data = filteredCarData.map((car) => {
                                return {
                                        x: car[dataKey].map((time) => time / 10000),
                                        type: "box",
                                        name: `${car.car_name}   (${car[dataKey].length})`,
                                        boxpoints: false,
                                        marker: {
                                                color: carColors[car.car_name],
                                        },
                                };
                        });

                        let totalSampleSize = 0;
                        filteredCarData.forEach((car) => {
                                totalSampleSize += car[dataKey].length;
                        });

                        const layout = {
                                title: {
                                        font: {
                                                color: "rgba(200, 200, 200, 1)",
                                                size: window.innerWidth > 800 ? 15 : 11,
                                        },
                                        text: `${trackName} (${totalSampleSize} entrants) ${titleSuffix}`,
                                },
                                xaxis: {
                                        title: "Lap Time (seconds)",
                                        titlefont: {
                                                color: "rgba(200, 200, 200, 1)",
                                                size: window.innerWidth > 800 ? 12 : 10, // Adjust the size as needed
                                        },
                                },
                                yaxis: {
                                        showticklabels: false,
                                },
                                hovermode: false,
                                legend: {},
                        };

                        const darkModeLayout = {
                                ...layout,
                                plot_bgcolor: "#25242750",
                                paper_bgcolor: "#25242750",
                                xaxis: {
                                        ...layout.xaxis,
                                        gridcolor: "rgba(80, 80, 80, 1)",
                                        tickfont: {
                                                color: "rgba(200, 200, 200, 1)",
                                        },
                                },
                                yaxis: {
                                        ...layout.yaxis,
                                        gridcolor: "rgba(80, 80, 80, 1)",
                                },
                                titlefont: {
                                        color: "rgba(200, 200, 200, 1)",
                                },
                                legend: {
                                        ...layout.legend,
                                        font: {
                                                color: "rgba(200, 200, 200, 1)",
                                                size: window.innerWidth > 800 ? 12 : 11,
                                        },
                                        orientation: window.innerWidth < 800 ? "h" : "v",
                                        x: window.innerWidth < 800 ? 0 : layout.legend.x,
                                        y: window.innerWidth < 800 ? -0.4 : layout.legend.y,
                                },
                                width: window.innerWidth > 800 ? 1200 : window.innerWidth,
                                height: 500,
                                margin: window.innerWidth > 800 ? { l: 40, r: 40, t: 60, b: 100 } : { l: 10, r: 8, t: 30, b: 240 },
                                autosize: false,
                        };

                        return (
                                <div key={trackName} className="box-plot-container">
                                        <Plot data={data} layout={darkModeLayout} config={{ displayModeBar: false, staticPlot: true }} />
                                </div>
                        );
                });
        };

        const racePlots = createPlots(groupedCarData, "race_best", "(Race Best)");
        const qualPlots = createPlots(groupedCarData, "qual_best", "(Qual Best)");

        if (isLoading) {
                return (
                        <div>
                                <div className="spinner"></div>
                                <div className="loading-text-div-bpc">Loading data...</div>
                        </div>
                );
        } else {
                return (
                        <div className="plots-container">
                                <div className="top-text-container">
                                        <h2 className="season-name-text">{decodeURI(season)}</h2>
                                        <h3 className="date-text">Last Updated: {lastUpdated.last_updated}</h3>
                                </div>
                                <div className="quali-race-choice-cont">
                                        <button onClick={() => setDisplayRaceBest(false)} className={!displayRaceBest ? "quali-race-active" : "quali-race-inactive"}>
                                                Qualifying
                                        </button>
                                        <button onClick={() => setDisplayRaceBest(true)} className={displayRaceBest ? "quali-race-active" : "quali-race-inactive"}>
                                                Race
                                        </button>
                                </div>
                                <div className="current-week-box" onClick={handleCurrentWeekLink}>
                                        <img src={line_chart_white} alt="Line Chart Icon" style={{ verticalAlign: "middle", marginRight: "1rem", marginLeft: ".15rem", height: "1.8rem" }} />
                                        See the current week in further detail ➔<div className="notification-bubble">new</div> {/* REMOVE LATER */}
                                </div>
                                {displayRaceBest ? racePlots.reverse() : qualPlots.reverse()}
                                <div className="footer-text-chart-page">Calculated using best race lap times.</div>
                                <div className="disclaimer-text-bpc">
                                        Box plots offer an imperfect overview of data distribution. Keep in mind other variables such as number of entries, visible in the legend.{" "}
                                </div>
                        </div>
                );
        }
};
